import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { brandStory, testimonials, contactInfo, faqList } from '@/mocks/aboutData';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '糖豆视频',
    url: 'https://www.tangdoushipin.com',
    description: '糖豆视频免费在线观看平台，聚合西瓜视频免费、小蓝视频、茄子视频、芒果视频等站最新电影电视剧综艺资源',
    email: contactInfo.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '望京科技园',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqList.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于糖豆视频',
    description: '糖豆视频品牌故事、用户口碑评价与联系方式',
    url: 'https://www.tangdoushipin.com/about',
  };

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(aboutPageSchema)}</script>

      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={[
              { name: '首页', path: '/' },
              { name: '关于我们' },
            ]} />
            <div className="text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">
                {brandStory.title}
              </h1>
              <p className="text-foreground-500 text-sm md:text-base">{brandStory.subtitle}</p>
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 items-start">
              <div className="lg:w-2/5 flex-shrink-0">
                <div className="aspect-[4/3] rounded-xl overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Modern%20Chinese%20tech%20office%20with%20creative%20team%20collaborating%2C%20warm%20natural%20light%20through%20large%20windows%2C%20plants%20and%20wooden%20furniture%2C%20friendly%20atmosphere%2C%20professional%20environment&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="糖豆视频团队"
                    className="w-full h-full object-cover"
                    title="糖豆视频团队"
                  />
                </div>
              </div>
              <div className="lg:w-3/5">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">品牌故事</h2>
                <div className="text-sm text-foreground-700 leading-relaxed space-y-4">
                  {brandStory.description.split('\n\n').map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-6">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                    {brandStory.founded}
                  </span>
                  <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                    {brandStory.mission}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">我们的理念</h2>
              <p className="text-foreground-500 text-sm">始终坚持为用户提供最好的观影体验</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {brandStory.values.map((value) => (
                <div key={value.title} className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-100">
                    <i className={`${value.icon} text-primary-600 text-xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">{value.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">用户评价</h2>
              <p className="text-foreground-500 text-sm">听听用户们怎么说</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{testimonial.content}</p>
                  <div className="flex items-center gap-3 pt-3 border-t border-background-200/70">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${testimonial.avatar} text-primary-600 text-sm`}></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-900">{testimonial.name}</div>
                      <div className="text-xs text-foreground-400">{testimonial.role} · {testimonial.date}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">常见问题</h2>
              <p className="text-foreground-500 text-sm">关于糖豆视频的常见疑问解答</p>
            </div>
            <div className="flex flex-col gap-3">
              {faqList.map((faq, index) => (
                <details key={index} className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between px-5 py-4 text-sm font-medium text-foreground-900 list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="w-full px-4 md:px-8 py-10 md:py-16">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">联系我们</h2>
              <p className="text-foreground-500 text-sm">如有任何问题或建议，欢迎联系我们</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-mail-line text-primary-600"></i>
                </div>
                <div className="text-xs text-foreground-400 mb-1">电子邮箱</div>
                <div className="text-sm font-medium text-foreground-900">{contactInfo.email}</div>
              </div>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-wechat-line text-accent-600"></i>
                </div>
                <div className="text-xs text-foreground-400 mb-1">微信客服</div>
                <div className="text-sm font-medium text-foreground-900">{contactInfo.wechat}</div>
              </div>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-secondary-100">
                  <i className="ri-qq-line text-secondary-600"></i>
                </div>
                <div className="text-xs text-foreground-400 mb-1">QQ群号</div>
                <div className="text-sm font-medium text-foreground-900">{contactInfo.qq}</div>
              </div>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-lg bg-primary-100">
                  <i className="ri-time-line text-primary-600"></i>
                </div>
                <div className="text-xs text-foreground-400 mb-1">工作时间</div>
                <div className="text-sm font-medium text-foreground-900">{contactInfo.workingHours}</div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}